import React, { ErrorInfo } from 'react';
import styled from '@emotion/styled';

import { ReactComponent as ImgBrokenComputer } from './svg/imgBrokenComputer.svg';
import './globalErrorHandler';
import { isFalsePositiveMessage } from './helpers';

/* ======================================== */

type TProps = React.PropsWithChildren<{
    name?: string;
}>

type TState = {
    hasError: boolean;
    error?: Error;
}

/* ======================================== */

const StyledErrorOverlay = styled.div(
    ({}) => ({
        position: 'relative',
        inset: 0,
        height: '100%',
        backgroundImage: 'repeating-linear-gradient(135deg, #eee 0, #ddd 30px, #eee 60px)',
        textAlign: 'center',
        opacity: 0.6
    })
);

const StyledImgBrokenComputer = styled(ImgBrokenComputer)(
    () => ({
        width: '20vw',
        height: '20vw',
        maxWidth: 200,
        maxHeight: 200
    })
);

const StyledDiv = styled.div(
    () => ({
        fontSize: '1.8rem'
    })
);

/* ======================================== */

export class ErrorBoundary extends React.PureComponent<TProps, TState> {
    constructor(props: TProps) {
        super(props);
        this.state = { hasError: false };
    }

    public static getDerivedStateFromError(error: Error): TState {
        if (isFalsePositiveMessage(error.message)) {
            return { hasError: false };
        }
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        if (!isFalsePositiveMessage(error.message)) {
            console.error(`Exception Caught in ${this.props.name}`, error, errorInfo);
        }
    }

    render() {
        if (this.state.hasError) {
            return (
                <StyledErrorOverlay>
                    <StyledImgBrokenComputer />
                    <StyledDiv>Sorry, something went wrong!</StyledDiv>
                </StyledErrorOverlay>
            );
        }

        return this.props.children;
    }
}
